import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

type QueryValue = string | number | boolean | null | undefined;
type Href =
  | string
  | {
      pathname?: string;
      query?: Record<string, QueryValue | QueryValue[]>;
      hash?: string;
    };

export interface NextLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: Href;
  children?: ReactNode;
  replace?: boolean;
  scroll?: boolean;
  prefetch?: boolean | null;
  shallow?: boolean;
  passHref?: boolean;
  legacyBehavior?: boolean;
  locale?: string | false;
}

function normalizeHref(href: Href): string {
  if (typeof href === "string") return href;

  const pathname = href.pathname || "/";
  const search = new URLSearchParams();

  for (const [key, rawValue] of Object.entries(href.query || {})) {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    for (const value of values) {
      if (value !== null && value !== undefined) search.append(key, String(value));
    }
  }

  const query = search.toString();
  const hash = href.hash ? (href.hash.startsWith("#") ? href.hash : `#${href.hash}`) : "";
  return `${pathname}${query ? `?${query}` : ""}${hash}`;
}

function isExternalHref(href: string): boolean {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);
}

const NextLink = forwardRef<HTMLAnchorElement, NextLinkProps>(function NextLink(
  {
    href,
    replace,
    scroll = true,
    prefetch: _prefetch,
    shallow: _shallow,
    passHref: _passHref,
    legacyBehavior: _legacyBehavior,
    locale: _locale,
    onClick,
    children,
    ...anchorProps
  },
  ref,
) {
  const normalizedHref = normalizeHref(href);

  if (
    isExternalHref(normalizedHref) ||
    anchorProps.target === "_blank" ||
    anchorProps.download
  ) {
    return (
      <a ref={ref} href={normalizedHref} onClick={onClick} {...anchorProps}>
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      ref={ref}
      to={normalizedHref}
      replace={replace}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented && scroll) {
          requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0 }));
        }
      }}
      {...anchorProps}
    >
      {children}
    </RouterLink>
  );
});

export default NextLink;
